import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";

const VARIANTS = [
  { u: 3, v: 5, i: 1, j: 9, k: 41 },
  { u: 2, v: 1, i: 1, j: 7, k: 37 },
  { u: 2, v: 1, i: 2, j: 7, k: 22 },
  { u: 2, v: 2, i: 1, j: 5, k: 17 },
  { u: 2, v: 2, i: 2, j: 8, k: 26 },
  { u: 2, v: 3, i: 1, j: 6, k: 21 },
  { u: 2, v: 4, i: 1, j: 7, k: 25 },
  { u: 2, v: 4, i: 2, j: 10, k: 34 },
];

const pick = (items) => items[Math.floor(Math.random() * items.length)];
const term = (index) => `a_{${index}}`;

export function generateTask(options = {}) {
  const original = options.variant === "original" && options.session === "2026_MAJ";
  const data = original ? VARIANTS[0] : pick(VARIANTS);
  return {
    type: "MaturaLinearSequenceGeometricTripletOpenTask",
    data: { ...data, maxPoints: 3 },
    source: {
      variant: original ? "original" : "random",
      exam: "MATURA",
      session: original ? "2026_MAJ" : undefined,
      taskNumber: "15",
      basedOn: "MMAP-P0-100-A-2605-15",
    },
  };
}

function getRaw(value) {
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (value && typeof value === "object") {
    if (value.raw && typeof value.raw === "object") return getRaw(value.raw);
    for (const key of ["raw", "value", "text"]) {
      if (typeof value[key] === "string" && value[key].trim()) return value[key];
      if (typeof value[key] === "number") return String(value[key]);
    }
  }
  return "";
}

const numberValue = (value) => Number(getRaw(value).replace(",", "."));

function getFinalValue(value) {
  if (value?.k !== undefined) return value.k;
  if (typeof value === "string" || typeof value === "number" || value?.raw !== undefined) return value;
  return "";
}

export function renderTask({ data }) {
  return (
    <div>
      <p>Dany jest ciąg <InlineMath math="(a_n)" /> określony wzorem</p>
      <BlockMath math={`a_n=${data.u}n+${data.v}.`} />
      <p>Wyznacz liczbę naturalną <InlineMath math="k" />, dla której liczby <InlineMath math={`${term(data.i)},${term(data.j)},a_k`} /> w podanej kolejności tworzą ciąg geometryczny.</p>
    </div>
  );
}

export function renderInput({ data }, value = {}, onChange, disabled) {
  const structuredValue = value && typeof value === "object" ? value : {};
  const set = (key, next) => onChange?.({ ...structuredValue, [key]: next });
  const legacyFinalValue = getFinalValue(value);
  const field = (fieldKey, prefix, label, fieldValue = structuredValue[fieldKey]) => {
    const raw = getRaw(fieldValue);
    return (
      <div style={{ display: "grid", gap: 6 }}>
        <div><InlineMath math={prefix} /></div>
        <FractionInput
          value={fieldValue || ""}
          onChange={(next) => set(fieldKey, next)}
          disabled={disabled}
          label={label}
          showPreview={false}
        />
        {raw ? <div><strong>Podgląd:</strong> <InlineMath math={`${prefix}${raw}`} /></div> : null}
      </div>
    );
  };

  return (
    <div style={{ display: "grid", gap: 16, maxWidth: 680 }}>
      <OpenTaskScoringHint variant="optional" />
      <div style={{ display: "grid", gap: 8 }}>
        <strong>Krok 1. Oblicz dwa znane wyrazy ciągu.</strong>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
          {field("knownFirst", `${term(data.i)}=`, `wartość ${term(data.i)}`)}
          {field("knownMiddle", `${term(data.j)}=`, `wartość ${term(data.j)}`)}
        </div>
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        <strong>Krok 2. Korzystając z własności ciągu geometrycznego, wyznacz wartość trzeciego wyrazu.</strong>
        {field("requiredTerm", "a_k=", "wartość wyrazu a k")}
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        <strong>Krok 3. Wyznacz numer tego wyrazu.</strong>
        {field("k", "k=", "wartość k", legacyFinalValue)}
      </div>
    </div>
  );
}

export function checkAnswer({ data }, value = {}) {
  const firstValue = data.u * data.i + data.v;
  const middleValue = data.u * data.j + data.v;
  const requiredTermValue = data.u * data.k + data.v;
  const knownTermsCorrect =
    Math.abs(numberValue(value.knownFirst) - firstValue) < 1e-9 &&
    Math.abs(numberValue(value.knownMiddle) - middleValue) < 1e-9;
  const requiredTermCorrect = Math.abs(numberValue(value.requiredTerm) - requiredTermValue) < 1e-9;
  const finalValue = getFinalValue(value);
  const finalCorrect = Math.abs(numberValue(finalValue) - data.k) < 1e-9;
  const score = finalCorrect ? 3 : requiredTermCorrect ? 2 : knownTermsCorrect ? 1 : 0;
  return {
    correct: score === 3,
    score,
    maxScore: 3,
    details: {
      "znane wyrazy ciągu": knownTermsCorrect,
      "wartość trzeciego wyrazu": requiredTermCorrect,
      "numer szukanego wyrazu": finalCorrect,
    },
  };
}

export function renderAnswer({ data }) {
  const firstValue = data.u * data.i + data.v;
  const middleValue = data.u * data.j + data.v;
  const requiredTermValue = data.u * data.k + data.v;
  return (
    <div style={{ display: "grid", gap: 4, textAlign: "left" }}>
      <InlineMath math={`${term(data.i)}=${firstValue},\\quad ${term(data.j)}=${middleValue}`} />
      <InlineMath math={`a_k=${requiredTermValue}`} />
      <InlineMath math={`k=${data.k}`} />
    </div>
  );
}

export function renderUserAnswer(value = {}, { data } = {}) {
  const finalValue = getFinalValue(value);
  const rows = [
    ["Pierwszy znany wyraz", data ? `${term(data.i)}=` : "", getRaw(value.knownFirst)],
    ["Drugi znany wyraz", data ? `${term(data.j)}=` : "", getRaw(value.knownMiddle)],
    ["Trzeci wyraz", "a_k=", getRaw(value.requiredTerm)],
    ["Numer wyrazu", "k=", getRaw(finalValue)],
  ];
  return (
    <div style={{ display: "grid", gap: 8, textAlign: "left" }}>
      {rows.map(([label, prefix, raw]) => (
        <div key={label}>
          <strong>{label}:</strong>{" "}
          <InlineMath math={`${prefix}${raw || "\\text{–}"}`} />
        </div>
      ))}
    </div>
  );
}

export function buildExplanationModel(data) {
  const ai = data.u * data.i + data.v;
  const aj = data.u * data.j + data.v;
  const square = aj ** 2;
  const requiredTerm = data.u * data.k + data.v;
  return {
    knownTerms: `${term(data.i)}=${data.u}\\cdot${data.i}+${data.v}=${ai},\\qquad ${term(data.j)}=${data.u}\\cdot${data.j}+${data.v}=${aj}`,
    unknownTerm: `a_k=${data.u}k+${data.v}`,
    geometricProperty: `(b_n)^2=b_{n-1}\\cdot b_{n+1}`,
    appliedProperty: `${term(data.j)}^2=${term(data.i)}\\cdot a_k`,
    substitution: `${aj}^2=${ai}\\cdot a_k`,
    requiredTermEquation: `${square}=${ai}a_k\\Longrightarrow a_k=${requiredTerm}`,
    indexEquation: `${data.u}k+${data.v}=${requiredTerm}`,
    isolatedEquation: `${data.u}k=${requiredTerm - data.v}`,
    result: `k=${data.k}`,
  };
}

export function renderExplanation({ data }) {
  const model = buildExplanationModel(data);
  return (
    <div style={{ display: "grid", gap: 12 }}>
      <p><strong>Krok 1.</strong> Obliczamy dwa znane wyrazy ciągu.</p>
      <BlockMath math={model.knownTerms} />

      <p><strong>Krok 2.</strong> Dla trzech kolejnych wyrazów ciągu geometrycznego kwadrat środkowego wyrazu jest równy iloczynowi wyrazów sąsiednich.</p>
      <BlockMath math={model.geometricProperty} />
      <p>W tym zadaniu środkową liczbą jest <InlineMath math={term(data.j)} />, zatem:</p>
      <BlockMath math={model.appliedProperty} />

      <p><strong>Krok 3.</strong> Podstawiamy obliczone wyrazy i wyznaczamy wartość trzeciego wyrazu.</p>
      <BlockMath math={model.substitution} />
      <BlockMath math={model.requiredTermEquation} />

      <p><strong>Krok 4.</strong> Korzystamy ze wzoru ogólnego danego ciągu i ustalamy numer wyrazu o obliczonej wartości.</p>
      <BlockMath math={model.unknownTerm} />
      <BlockMath math={model.indexEquation} />
      <BlockMath math={model.isolatedEquation} />
      <BlockMath math={model.result} />

      <p><strong>Wniosek.</strong> Szukaną liczbą naturalną jest <InlineMath math={`k=${data.k}`} />.</p>
    </div>
  );
}
